// ============================================================
// THE SETTINGS REGISTRY - every switch this app offers, declared
// once as data.
//
// The GM dialog was a layout: 12 locks, 19 booleans and 3
// dropdowns in one flat scroll, in whatever order they were added
// in. Adding a ruling meant appending to a 113-line table inside a
// component and hoping somebody found it.
//
// So the settings become a TABLE and the dialog becomes a view
// over it. Sections, search, the changed-from-default list and the
// E2E helper are all `filter()` over this array - none of them is
// a second list to keep in step.
//
// WHAT LIVES HERE AND WHAT DOES NOT. `defaultOn` is the mechanical
// half and it must agree with the resolver in multiplayer.ts,
// which stays the authority every consumer reads -
// `registry.correspondence` in multiplayer.test.js asserts that for
// every key. Nothing here decides anything; it only says what may
// be decided, and where it is shown.
//
// Pure. Zero React dependency.
// ============================================================
import { COVER_CONFLICT_MODES } from '../coverConflict';
import { COVER_NEAR_TARGET_CHOICES, DEFAULT_COVER_NEAR_TARGET } from '../battlefield/coverNearTarget';
import { VEHICLE_ARC_PRESETS } from '../battlefield/facing';
import { AVERAGED_DAMAGE_MODES, DEFAULT_AVERAGED_DAMAGE } from '../survival/averagedDamage';
import { CHALLENGE_FAILURES, DEFAULT_CHALLENGE_FAILURE } from '../killTeam/houseRules';
import { DEFAULT_UNNATURAL_DOS_MODE, UNNATURAL_DOS_MODES } from '../unnaturalDos';
import { ASSIST_BONUSES, DEFAULT_ASSIST_BONUS } from '../narrative/assist';
import { DEFAULT_SIGNATURE_REACH_KEY, SIGNATURE_REACHES } from '../stealth/noise';
import { REACTION_ALARM_POLICIES, DEFAULT_REACTION_ALARM } from '../reactionAlarm';
import { STACK_POLICIES, DEFAULT_STACK_POLICY } from '../stackPolicy';
import { BLAST_SELF_POLICIES, DEFAULT_BLAST_SELF } from '../blastRules';
import {
  DEFAULT_INITIATIVE_ARRIVAL,
  DEFAULT_INITIATIVE_TIE_BREAK,
  INITIATIVE_ARRIVALS,
  INITIATIVE_TIE_BREAKS,
} from '../initiative';
import { TERRAIN_ANIMATIONS } from '../battlefield/terrainAnimation';
import { MOVEMENT_BAND_LOOKS } from '../battlefield/movementBandLook';
import { MARKER_STATUS_LEVELS } from '../battlefield/markerStatus';
import { MOTION_CHOICES } from '../phase/motion';

/**
 * WHO OWNS THE ANSWER - the top-level split, and the reason there
 * are two dialogs rather than one long one.
 *
 * A `session` setting is one ruling for the whole table, written to
 * the shared permissions document by the GM. A `viewer` setting is
 * one person's own view of it and never leaves their machine.
 */
export type SettingOwner = 'session' | 'viewer';

export type SettingSection =
  | 'rules'
  | 'turns'
  | 'map'
  | 'deployment'
  | 'table'
  | 'protagonists'
  | 'table-display'
  | 'mine-map'
  | 'mine-display'
  | 'mine-log'
  | 'mine-turns'
  | 'mine-screen';

export interface SectionDef {
  id: SettingSection;
  owner: SettingOwner;
  title: string;
  /** One line under the heading - what kind of thing belongs here. */
  blurb: string;
}

/** The rooms, in the order they are offered. */
export const SECTIONS: SectionDef[] = [
  {
    id: 'rules',
    owner: 'session',
    title: 'Rules',
    blurb: 'How the dice and the conditions behave, for everyone at this table.',
  },
  {
    id: 'turns',
    owner: 'session',
    title: 'Turn order',
    blurb: 'Who acts when, how that order is arrived at, and what ticks as it passes.',
  },
  {
    id: 'map',
    owner: 'session',
    title: 'Battlefield',
    blurb: 'What the tactical map is allowed to decide, and what moving about costs.',
  },
  {
    id: 'deployment',
    owner: 'session',
    title: 'Deployment',
    blurb: 'Where the app may put people when it places them for you.',
  },
  {
    id: 'table',
    owner: 'session',
    title: 'At the table',
    blurb: 'What the app does on its own, and how much of it is on screen.',
  },
  {
    id: 'protagonists',
    owner: 'session',
    title: 'Protagonists',
    blurb: 'Whether named characters may carry survival rules, and how much the table is told.',
  },
  {
    id: 'table-display',
    owner: 'session',
    title: "The table's display",
    blurb:
      "Where each screen's display starts, until its viewer chooses for themselves. A viewer's own choice always wins.",
  },
  {
    id: 'mine-map',
    owner: 'viewer',
    title: 'My map',
    blurb: 'How the battlefield is drawn for you. Yours alone - nobody else sees these change.',
  },
  {
    id: 'mine-display',
    owner: 'viewer',
    title: 'My display',
    blurb: "How the display frames the action for you. Yours alone; the GM may set where the table's screens start.",
  },
  {
    id: 'mine-log',
    owner: 'viewer',
    title: 'My log',
    blurb: 'Which log you are reading. Also yours alone.',
  },
  {
    id: 'mine-turns',
    owner: 'viewer',
    title: 'My turns',
    blurb: 'How the order moves you about. Yours alone.',
  },
  {
    id: 'mine-screen',
    owner: 'viewer',
    title: 'My screen',
    blurb: 'How the modes look on your screen. Yours alone.',
  },
];

interface SettingChoice {
  key: string;
  label: string;
  hint?: string;
}

type SettingControl =
  { kind: 'toggle'; defaultOn: boolean } | { kind: 'enum'; choices: SettingChoice[]; fallback: string };

export interface SettingDef {
  key: string;
  section: SettingSection;
  owner: SettingOwner;
  /** The EFFECT, in the table's language - not the mechanism. */
  label: string;
  /**
   * The accessible name, when the visible label is not it.
   *
   * Only the enums need one: their control sits inside a `<label>` that
   * also carries the chosen ruling's explanation, and an accessible name
   * made of both would be a paragraph.
   */
  aria?: string;
  hint?: string;
  /**
   * The key this one has no effect without.
   *
   * Two hints used to say "Needs the rule above", which was true only
   * while nothing moved. Named rather than positional, so regrouping
   * cannot quietly turn the sentence into a lie.
   */
  needs?: string;
  /**
   * A sentence that belongs to the SETTING rather than to whichever
   * answer is chosen - shown after the chosen ruling's own hint.
   */
  note?: string;
  control: SettingControl;
  /** Extra words the search should match - the mechanism, the old name. */
  keywords?: string;
  /**
   * A VIEWER setting the GM may give the table a default for (ADR
   * 2026-10-07): a generated session row (`tableKeyOf`) holds it, and a
   * screen reads it only while its viewer has chosen nothing (`viewerChoice`).
   */
  tableDefault?: boolean;
}

/**
 * Every setting written by hand, in the order the GM meets it.
 *
 * The order inside a section is this array's order; the sections
 * themselves are `SECTIONS`.
 */
const ROWS: SettingDef[] = [
  // ---- Rules ----
  {
    key: 'unrestrictedWeaponSpecials',
    section: 'rules',
    owner: 'session',
    label: 'Melee & ranged weapons share all special rules',
    control: { kind: 'toggle', defaultOn: false },
    keywords: 'weapon specials tearing cross-type',
  },
  // ---- Squad Mode (2026-10-23) ----
  {
    key: 'cohesionChallengeFailure',
    section: 'rules',
    owner: 'session',
    label: 'A failed Cohesion Challenge',
    aria: 'Cohesion Challenge failure',
    hint: "The Challenge is a Free Action: a d10 at or under the Cohesion left lets a brother into Squad Mode at once. What a failure does is the table's call.",
    control: { kind: 'enum', choices: CHALLENGE_FAILURES, fallback: DEFAULT_CHALLENGE_FAILURE },
    keywords: 'squad mode cohesion challenge kill-team enter',
  },
  {
    key: 'cohesionDamage',
    section: 'rules',
    owner: 'session',
    label: 'Heavy hits cost the kill-team Cohesion',
    hint: 'A brother in Squad Mode hit for 10 or more before reduction by an Accurate, Blast or Devastating weapon costs the pool one - one per brother hit - unless the (acting) leader holds them with a Challenging Command or Fellowship test.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'squad mode cohesion damage blast devastating accurate kill-team',
  },
  {
    key: 'oneSquadAbilityPerTurn',
    section: 'rules',
    owner: 'session',
    label: "One squad ability's benefit per brother between his turns",
    hint: 'Starting or joining one marks him until his next turn. The GM may still press a dimmed plate (logged as a GM override).',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'squad mode ability once per turn kill-team oath',
  },
  {
    key: 'applyConditionModifiers',
    section: 'rules',
    owner: 'session',
    label: "Apply condition modifiers to a target's tests & reactions",
    hint: 'Stunned −20, Fatigue −10 a level, and the rest - on tests and Dodge/Parry. It also governs the map: Immobilised and Unconscious stop a combatant moving, a crippled leg halves them, Prone reduces them to a crawl, and a modifier scoped to Movement changes how far they get.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'conditions stunned fatigue penalties',
  },
  {
    key: 'showActionEconomy',
    section: 'rules',
    owner: 'session',
    label: 'Count the action economy (advisory)',
    hint: 'Two points a turn - a Full action spends both, a Half one, a Free none. The footer shows what is left while the turn tracker is running. NOTHING is ever blocked: tables that let a character exceed a Full action simply see the pips go past two.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'action economy points half full turn budget advisory pips',
  },
  {
    key: 'immobilisedAffectsCombat',
    section: 'rules',
    owner: 'session',
    label: 'Immobilised imposes −30 WS and prevents Dodge',
    hint: 'The rule as written, and what a vehicle has always answered to. Worth its own switch now that it reaches people too - a weapon that snares a combatant stores the condition, and someone who cannot dodge is in far more trouble than a tank that cannot.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'immobilised snare dodge vehicle',
  },
  {
    key: 'helplessRanged',
    section: 'rules',
    owner: 'session',
    label: 'A helpless target is hit automatically by ranged attacks too',
    hint: 'Against someone Unconscious, Paralysed, Dying or marked Helpless, a melee attack cannot miss and rolls its damage twice. As written that is for melee only. On, a shot gets the same: the roll is still made (the weapon can jam), a miss lands as one hit, and the damage is rolled twice.',
    control: { kind: 'toggle', defaultOn: false },
    keywords: 'helpless unconscious coup de grace execution takedown ranged double damage',
  },
  {
    key: 'conditionsBlockSheetTests',
    section: 'rules',
    owner: 'session',
    label: 'A condition that prevents a reaction also disables its sheet test',
    hint: 'Stunned, Unconscious, Paralysed and Immobilised grey out the Dodge or Parry button on the sheet and say why, rather than leaving a roll available that cannot mean anything. Off keeps them rollable with a dim tag, as an untrained skill is - for a GM who wants to call for the roll regardless.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'dodge parry sheet test blocked',
  },
  {
    key: 'unnaturalDosMode',
    section: 'rules',
    owner: 'session',
    label: 'Unnatural Characteristics & test DoS',
    aria: 'Unnatural DoS mode',
    note: 'Applies to sheet tests, attack rolls, defence reactions, and opposed tests.',
    control: { kind: 'enum', choices: UNNATURAL_DOS_MODES, fallback: DEFAULT_UNNATURAL_DOS_MODE },
    keywords: 'unnatural degrees of success multiplier',
  },
  {
    key: 'assistBonus',
    section: 'rules',
    owner: 'session',
    label: 'What each helper adds to a test',
    aria: 'Assist bonus',
    note: "Read by a called test's helpers and by the helpers of a group approach in Stealth.",
    control: { kind: 'enum', choices: [...ASSIST_BONUSES], fallback: DEFAULT_ASSIST_BONUS },
    keywords: 'assist help helper aid bonus group test approach',
  },
  {
    key: 'signatureReach',
    section: 'rules',
    owner: 'session',
    label: 'How far a weapon is heard',
    aria: 'Signature reach',
    note: 'Each weapon is silent, muffled, loud or deafening. In Stealth only the opposition within its reach hears it; a silent weapon is heard at arm\u2019s length.',
    control: { kind: 'enum', choices: [...SIGNATURE_REACHES], fallback: DEFAULT_SIGNATURE_REACH_KEY },
    keywords: 'signature noise loud silent muffled deafening stealth heard reach silencer',
  },
  {
    key: 'reactionAlarm',
    section: 'rules',
    owner: 'session',
    label: 'Who is interrupted when a reaction is owed',
    aria: 'Reaction alarm policy',
    note: 'Everyone sees every owed reaction either way, and anyone may answer one. This decides only who gets the alarm rather than the quiet entry.',
    control: { kind: 'enum', choices: REACTION_ALARM_POLICIES, fallback: DEFAULT_REACTION_ALARM },
    keywords: 'reaction alarm dodge parry prompt interrupt notify gm',
  },
  {
    key: 'dreadPerAttack',
    section: 'rules',
    owner: 'session',
    label: 'Dread is tested on every attack',
    hint: 'A Terrifying target (Fear, or a dread exposure) costs a Willpower test to attack. Off - the ruling - an attacker tests ONCE per target and remembers the answer; on, every attack is tested and nothing is remembered.',
    control: { kind: 'toggle', defaultOn: false },
    keywords: 'fear dread terrifying willpower exposure',
  },
  {
    key: 'blastScatter',
    section: 'rules',
    owner: 'session',
    label: 'A missed blast scatters',
    note: 'Direction on a d10 clock face, 1d5 metres per degree of failure. A melee blast never scatters - a swing that misses simply misses.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'blast scatter grenade miss deviate template',
  },
  {
    key: 'blastCatchesAttacker',
    section: 'rules',
    owner: 'session',
    label: 'A blast can catch the one who threw it',
    aria: 'Blast catches the attacker',
    note: 'Allies inside the template are always caught. This is only about the attacker themselves.',
    control: { kind: 'enum', choices: BLAST_SELF_POLICIES, fallback: DEFAULT_BLAST_SELF },
    keywords: 'blast friendly fire self attacker melee grenade scatter',
  },
  {
    key: 'vehicleArcPreset',
    section: 'rules',
    owner: 'session',
    label: "A vehicle's armour arcs",
    aria: 'Vehicle arc preset',
    control: { kind: 'enum', choices: VEHICLE_ARC_PRESETS, fallback: 'quarters' },
    keywords: 'vehicle facing front rear flank arc',
  },

  // ---- Turn order ----
  //
  // roundCountdown and autoResolveTurnEffects moved here in v8.71.0
  // from Rules and At the table respectively. Both are turn-tracker
  // rules that were filed elsewhere because the tracker had no room of
  // its own. The move costs nothing: setHouseRule looks the room up in
  // this table, so not one of the sixteen E2E call sites changes.
  {
    key: 'autoRollInitiative',
    section: 'turns',
    owner: 'session',
    label: 'Roll initiative when a combatant joins',
    hint: '1d10 + Agility Bonus, including any Unnatural Agility and talents like Preternatural Speed. Off, everyone arrives without a number and the order is whatever you make it - typing a value by hand still sorts.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'initiative roll d10 agility bonus automatic admission',
  },
  {
    key: 'initiativeArrivals',
    section: 'turns',
    owner: 'session',
    label: 'Someone joining mid-round acts\u2026',
    aria: 'Mid-round arrival behaviour',
    note: 'Whichever you pick, the order never runs backwards - the participant already acting keeps acting.',
    control: { kind: 'enum', choices: INITIATIVE_ARRIVALS, fallback: DEFAULT_INITIATIVE_ARRIVAL },
    keywords: 'reinforcements join mid-round arrive spawn late',
  },
  {
    key: 'initiativeTieBreak',
    section: 'turns',
    owner: 'session',
    label: 'Two equal initiatives are separated by\u2026',
    aria: 'Initiative tie-break',
    control: { kind: 'enum', choices: INITIATIVE_TIE_BREAKS, fallback: DEFAULT_INITIATIVE_TIE_BREAK },
    keywords: 'tie break equal same initiative order',
  },
  {
    key: 'rerollEachRound',
    section: 'turns',
    owner: 'session',
    label: 'Everyone rerolls initiative each round',
    hint: 'The order is redrawn at the start of every round. Deathwatch rolls once and keeps it, so this is off by default - but it does make mid-combat arrivals a non-question.',
    needs: 'autoRollInitiative',
    control: { kind: 'toggle', defaultOn: false },
    keywords: 'reroll every round variant redraw order',
  },
  {
    key: 'rerollKeepsManual',
    section: 'turns',
    owner: 'session',
    label: 'A bulk reroll leaves hand-typed initiatives alone',
    hint: 'Off, "reroll all" means all. On, a number somebody typed is a ruling the dice may not overturn - the \u21bb on that chip still rerolls it deliberately.',
    control: { kind: 'toggle', defaultOn: false },
    keywords: 'reroll manual override typed protect sticky',
  },
  {
    key: 'reliableNeverJams',
    section: 'rules',
    owner: 'session',
    label: 'Reliable weapons never jam at all',
    hint: 'Off (the book): a Reliable weapon jams only on a natural 00, and even that needs a 96+ on a confirming d100. On: it simply never jams - the sim\u2019s behaviour before v8.197.17.',
    control: { kind: 'toggle', defaultOn: false },
    keywords: 'reliable jam never confirm 00 misfire',
  },
  {
    key: 'roundCountdown',
    section: 'turns',
    owner: 'session',
    label: 'Timed conditions count down each round',
    hint: 'Stunned 3, Blinded 2, Dying 1 and scoped modifiers lose a round at the end of a tracked turn, or on each attack when the turn tracker is off.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'duration expire stunned blinded dying',
  },
  {
    key: 'stackPolicy',
    section: 'rules',
    owner: 'session',
    label: 'A condition applied to somebody who already has it',
    aria: 'Stacking policy',
    note: 'The answer when neither the picker nor the weapon effect names one. A custom modifier merges by its SCOPE - Movement +10 and Movement +20 are the same thing to merge - and the policy says how.',
    control: { kind: 'enum', choices: STACK_POLICIES, fallback: DEFAULT_STACK_POLICY },
    keywords: 'stack stacking refresh extend raise additive duration merge modifier',
  },
  {
    key: 'recallKeepsTurn',
    section: 'turns',
    owner: 'session',
    label: 'Recalling someone to a waiting room keeps their place in the order',
    hint: 'Off, pulling somebody off the board takes them out of the turn order too - they are not there to act. On, the room is a wing rather than an exit and they keep their turn, which suits a table that shuttles people in and out of one fight.',
    control: { kind: 'toggle', defaultOn: false },
    keywords: 'waiting room recall withdraw remove turn order',
  },
  {
    key: 'skipDownedTurns',
    section: 'turns',
    owner: 'session',
    label: 'Skip the turns of the dead and the unconscious',
    hint: 'They stay in the order at the initiative they already had, dimmed, and are only passed over - so reviving one puts them straight back with nothing to re-enter. Off gives them a turn to play out.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'dead killed unconscious skip pass over dying',
  },
  {
    key: 'autoResolveTurnEffects',
    section: 'turns',
    owner: 'session',
    label: 'Roll start-of-turn effect tests automatically (On Fire, etc.)',
    control: { kind: 'toggle', defaultOn: false },
    keywords: 'turn tracker on fire automatic rolls',
  },

  // ---- Battlefield ----
  {
    key: 'battlefieldDerivedValues',
    section: 'map',
    owner: 'session',
    label: 'The battlefield map can set cover and range',
    hint: 'Measuring on the map offers an Apply button that writes the cover and range band it found onto the target. Applied values clear themselves whenever a token moves or the round turns; cover you set by hand is never touched. Off leaves the map purely informative.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'cover range derived apply',
  },
  {
    key: 'autoApplyMapModifiers',
    section: 'map',
    owner: 'session',
    label: 'The map sets range and cover automatically',
    hint: 'The actor→target line is measured continuously, so the range band and the cover follow everyone about without anyone clicking Apply. Each attacker gets their own answer - two shooters on opposite sides of a barricade do not share a sightline. Pin a value on the map readout to overrule it for a shot.',
    needs: 'battlefieldDerivedValues',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'automatic range cover measure',
  },
  {
    key: 'ignoreTerrain',
    section: 'map',
    owner: 'session',
    label: 'Terrain adjudicates nothing - the map is a diagram',
    hint: 'Cover, line of sight, movement and the climb all stop consulting the map, for a table that wants the picture without the geometry deciding their fights. A combatant who ignores terrain individually is unaffected either way.',
    control: { kind: 'toggle', defaultOn: false },
    keywords: 'terrain geometry diagram cover line of sight',
  },
  {
    key: 'coverNearTarget',
    section: 'map',
    owner: 'session',
    label: 'Cover counts only near the target',
    aria: 'Cover near the target',
    hint: "Terrain gives cover only when it stands this close to the target, measured from the edge of his footprint along the ground. Farther terrain still blocks sight and still slows a round; it covers nobody. Smoke hides you wherever it hangs. 'Anywhere' is the old rule, where a berm at the shooter's feet covered his target.",
    control: { kind: 'enum', choices: COVER_NEAR_TARGET_CHOICES, fallback: DEFAULT_COVER_NEAR_TARGET },
    keywords: 'cover near target distance berm metres',
  },
  {
    key: 'coverConflictMode',
    section: 'map',
    owner: 'session',
    label: "Cover set by hand, against the map's",
    aria: 'Cover conflict mode',
    control: { kind: 'enum', choices: COVER_CONFLICT_MODES, fallback: 'highest' },
    keywords: 'cover pinned conflict highest',
  },
  {
    key: 'surfacesModifyCombatants',
    section: 'map',
    owner: 'session',
    label: 'Painted ground modifies the combatants standing on it',
    hint: 'A surface can carry condition codes - a penalty, a status, even On Fire - and grant them to whoever stands on it, ending the moment they walk out. Nothing is written to the combatant. Off leaves painted ground as colour and commentary, for a table that keeps the rules in their heads. A flyer is over it and a burrower under it unless the surface says otherwise.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'surfaces terrain paint fire mud',
  },
  {
    key: 'fatigueSlowsMovement',
    section: 'map',
    owner: 'session',
    label: 'Fatigue also slows a combatant on the map',
    hint: 'A step of movement per level. By the book Fatigue is −10 to characteristic TESTS and movement comes from the characteristic rather than a test, so strictly it does not slow anybody - this is the ruling most tables make anyway.',
    needs: 'applyConditionModifiers',
    control: { kind: 'toggle', defaultOn: false },
    keywords: 'fatigue movement slow',
  },
  {
    key: 'climbCostsMovement',
    section: 'map',
    owner: 'session',
    label: 'Climbing costs movement',
    hint: 'Going up is charged against the move: a metre of rise costs metres of movement, so a staircase is no longer as cheap as the same distance of flat floor. A hoverer pays nothing - it already skims what would slow it - and a flyer never meets the question. Off, every rise within a stride is free, as it was before v7.95.0.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'climb ladder stairs rise',
  },
  {
    key: 'levelPickerSpendsMovement',
    section: 'map',
    owner: 'session',
    label: 'Choosing a storey costs the climb',
    hint: 'The level picker was a free lift: reaching a gantry cost eight metres by drag and nothing by list, and the list was one click. On, each storey shows its price and greys when it is more than the chosen action allows. Coming DOWN is free either way - that is what stops anyone being stranded on a roof. Anyone holding "Free placement" can Alt-click a storey to place without spending.',
    needs: 'climbCostsMovement',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'storey level picker gantry roof',
  },
  {
    key: 'climbTrainingSetsRate',
    section: 'map',
    owner: 'session',
    label: 'The Climb skill makes climbing cheaper',
    hint: 'Untrained pays 3 metres of movement per metre of rise, Trained 2, +10 pays 1.5 and +20 climbs as fast as they walk. No roll is asked for - training sets the rate, so a move is never interrupted for a die. Off, everyone pays the untrained rate.',
    needs: 'climbCostsMovement',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'climb skill trained rate',
  },

  // ---- Deployment ----
  {
    key: 'deployAvoidStructures',
    section: 'deployment',
    owner: 'session',
    label: 'Deployment avoids putting anyone inside a structure',
    hint: 'A slot inside a wall is rejected and the search steps outward along the same bearing. Off, they land where the ring says and carry the usual overlap warning. A combatant who ignores terrain - anything flying - skips this either way.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'deploy wall structure inside',
  },
  {
    key: 'deployAvoidImpassable',
    section: 'deployment',
    owner: 'session',
    label: 'Deployment avoids impassable ground',
    hint: 'Chasms, the void, anything movement calls impassable. Independent of the structures switch, because a pit and a wall are not the same problem.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'deploy chasm void impassable',
  },
  {
    key: 'deployAvoidCombatants',
    section: 'deployment',
    owner: 'session',
    label: 'Deployment keeps combatants out of each other',
    hint: 'Off by default: some tables genuinely stack models in a doorway or a transport, and the overlap warning appears either way. On, a slot within another combatant’s footprint is rejected and the search steps outward.',
    control: { kind: 'toggle', defaultOn: false },
    keywords: 'deploy overlap stack crowd',
  },
  {
    key: 'redeployOnMapLoad',
    section: 'deployment',
    owner: 'session',
    label: 'Loading a map rings everyone at the marker again',
    hint: 'Off, positions are kept and only settled - anyone the new ground rose under is stood on top of it, and anyone outside the new arena is pulled to its edge. On, a map swap re-rings the whole map at the marker, which is what you want when the old layout belonged to a different building.',
    control: { kind: 'toggle', defaultOn: false },
    keywords: 'map load redeploy marker ring',
  },

  // ---- At the table ----
  {
    key: 'detailedActorBodyMap',
    section: 'table',
    owner: 'session',
    label: "Show the acting character's body map in full detail",
    hint: 'Armour values, wounds, cover, crippled limbs and fire on your own silhouette, exactly as they appear on a target. Off gives a plain outline.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'body map actor silhouette armour',
  },
  {
    key: 'logYieldsInBuild',
    section: 'table',
    owner: 'session',
    label: 'The combat log gets out of the way while building',
    hint: 'Entering Build rolls the log away, because the build options sit across the top of the map and laying terrain out is not a moment anyone is reading attack narration. It comes back when you leave. Off leaves the log exactly where it is.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'combat log build deploy curtain',
  },
  {
    key: 'playersSeeMuster',
    section: 'table',
    owner: 'session',
    label: 'Players see the muster',
    hint: 'The muster is the column under the actor rail: everyone on the stage, one click to act as them. The GM always has it. On, players get it too (never your hidden figures); off, a player keeps only the chips of their own squad.',
    control: { kind: 'toggle', defaultOn: false },
    keywords: 'muster act as actor quick bar players',
  },

  // ---- Protagonists ----
  {
    key: 'protagonistRules',
    section: 'protagonists',
    owner: 'session',
    label: 'Named characters may carry survival rules',
    hint: "Honours whatever each character's Protagonist tab says - damage floors and ceilings, a critical cap, and the death floor that decides where a killing blow goes instead. Off, every one of those fields is inert and the dice behave exactly as they always did.",
    note: 'On by default. It changes nothing until a character is actually marked a protagonist, and off-by-default meant filling in the tab, playing, and watching the character die anyway. Sharing is guarded where it belongs: an export leaves these rules behind unless you ask for them.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'protagonist plot armour death floor survival named character',
  },
  {
    key: 'bandedWounds',
    section: 'protagonists',
    owner: 'session',
    label: 'Wounds are shown as a band, not a number',
    hint: 'Everyone reads Unhurt, Grazed, Wounded, Bloodied or Down - their own included - and the log describes damage rather than counting it. You still see the numbers.',
    note: 'A player who reads 7/22 does the arithmetic and relaxes. The log is redacted by the same switch, because a band beside a log that prints the damage is only decoration.',
    control: { kind: 'toggle', defaultOn: false },
    keywords: 'bands fog wounds hidden numbers redact log uncertainty',
  },
  {
    key: 'averagedDamage',
    section: 'protagonists',
    owner: 'session',
    label: 'Damage rolls take their average',
    aria: 'Averaged damage mode',
    note: 'There is no "enemies only": the app has no notion of sides, by design. Aiming it at protagonists is the closest honest equivalent.',
    control: { kind: 'enum', choices: AVERAGED_DAMAGE_MODES, fallback: DEFAULT_AVERAGED_DAMAGE },
    keywords: 'average mean swingy variance deterministic damage roll',
  },

  // ---- Mine: the map ----
  // NEVER SYNCED, and there is deliberately no GM suggestion for any of
  // them. Whether YOU want names on the map is not a table ruling, and a
  // suggestion that overwrites a stored choice is the v8.36.0 log bug
  // wearing a different hat.
  {
    key: 'markerStatus',
    section: 'mine-map',
    owner: 'viewer',
    label: 'How much a marker says about who is standing there',
    aria: 'Marker status detail',
    control: { kind: 'enum', choices: MARKER_STATUS_LEVELS, fallback: 'minimal' },
    keywords: 'marker wounds conditions ammo detail',
  },
  {
    key: 'showLabels',
    section: 'mine-map',
    owner: 'viewer',
    label: 'Name every marker on the map',
    hint: 'Off, a marker is a shape and a colour and you read the panels for the rest - which is what most tables want once they know who is who.',
    control: { kind: 'toggle', defaultOn: false },
    keywords: 'names labels markers',
  },
  {
    key: 'followTurn',
    section: 'mine-turns',
    owner: 'viewer',
    label: 'Act as whoever the order reaches',
    hint: "When the turn ends and the order reaches someone you may reasonably play - anyone, for the GM; the next combatant after your own turn; a unit in your control network - you act as them. Nobody's pointer is ever taken from them. Off unless you ask: most people keep to one character, the GM included.",
    // OFF by default since v8.331.5: the 2026-09-09 playtest read END TURN
    // moving the pointer as a bug. The GM running a horde turns it on.
    control: { kind: 'toggle', defaultOn: false },
    keywords: 'follow turn actor order tracker',
  },
  {
    key: 'autoTarget',
    section: 'mine-map',
    owner: 'viewer',
    label: 'Measuring at someone makes them your target',
    hint: 'Measuring to a combatant is a deliberate act and it is nearly always the person you are about to shoot. Off, the tape measures and nothing else happens.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'measure tape target selection',
  },
  {
    key: 'hoverInfo',
    section: 'mine-map',
    owner: 'viewer',
    label: 'Hovering says what you are looking at',
    hint: 'The name of a material, what a surface does, how hard a wall is. A GM who set a hardness a fortnight ago has no other way to ask what they chose.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'inspect hover tooltip material',
  },
  {
    key: 'hoverDetail',
    section: 'mine-map',
    owner: 'viewer',
    label: 'And what it means for the shot you are about to take',
    hint: 'A second layer of numbers - useful while you are working out an angle, noise the rest of the time.',
    needs: 'hoverInfo',
    control: { kind: 'toggle', defaultOn: false },
    keywords: 'inspect detail cover range numbers',
  },
  {
    key: 'terrainMarks',
    section: 'mine-map',
    owner: 'viewer',
    label: 'Hatch dangerous and impassable ground',
    hint: 'So a hazard cannot be missed and blocked ground cannot pass for open. Off by default: a GM who painted their own terrain has already said what it looks like, and a cross-hatch over the top argues with them.',
    control: { kind: 'toggle', defaultOn: false },
    keywords: 'hazard impassable hatching terrain marks',
  },
  {
    key: 'showPings',
    section: 'mine-map',
    owner: 'viewer',
    label: "Show other people's pings",
    hint: 'On by default - the whole point is that they reach you. Muting them here takes nothing away from the rest of the table; the GM has a lock for that.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'ping point marker attention',
  },
  {
    key: 'showSurfaceNames',
    section: 'mine-map',
    owner: 'viewer',
    label: 'Show surface names',
    hint: "Each painted surface's name on the ground, and the note under a ping saying what was pointed at. On by default; off for a quieter map. The log keeps every ping's note either way.",
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'surface name label ground floodlight ping note text',
  },
  {
    key: 'openTargetOnClick',
    section: 'mine-map',
    owner: 'viewer',
    label: 'Open the target panel when I click a figure',
    hint: 'A click on a figure on the map targets them, and with this on opens their panel; a click on bare ground closes it again. On by default.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'target panel open click figure marker rail',
  },

  // ---- Mine: the display (plan map-display, C; the GM may default each for the table) ----
  {
    key: 'displayFollow',
    section: 'mine-display',
    owner: 'viewer',
    label: 'The camera follows the action',
    hint: 'One move per turn, to a frame of who acts and whom they aim at, then it holds. A hand on the camera takes the wheel; "Follow the action" gives it back.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'camera follow focus shot turn two-shot',
    tableDefault: true,
  },
  {
    key: 'displayShoulder',
    section: 'mine-display',
    owner: 'viewer',
    label: 'Over the shoulder when your character acts',
    hint: 'Low, from behind and to the right of your own character, looking at its target. Every other turn keeps the two-shot.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'camera shoulder third person low angle',
    tableDefault: true,
  },
  {
    key: 'displayPerspective',
    section: 'mine-display',
    owner: 'viewer',
    label: 'Mild perspective',
    hint: 'Nearer things drawn larger. Off, the display measures like the classic map.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'camera perspective lens depth fov',
    tableDefault: true,
  },
  {
    key: 'displayQuiet',
    section: 'mine-display',
    owner: 'viewer',
    label: 'Quiet map, loud focus',
    hint: 'Whoever the action is not about - further than 8 m from the actor and the target - is drawn dim and unnamed. A target too far to frame gets an arrow at the edge.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'focus dim quiet crowd readable',
    tableDefault: true,
  },
  {
    key: 'displayPills',
    section: 'mine-display',
    owner: 'viewer',
    label: 'Names as pills',
    hint: "Names in a dark pill with a stripe of their side, stacked so no two are ever drawn over each other. Names still come and go with the map's Names switch.",
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'labels names pills readable',
    tableDefault: true,
  },
  {
    key: 'displayNumbersOff',
    section: 'mine-display',
    owner: 'viewer',
    label: 'Numbers off the map',
    hint: 'No wound numbers on the figures; the readout under the pointer still has them.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'numbers wounds clutter hover card',
    tableDefault: true,
  },
  {
    key: 'displayDepth',
    section: 'mine-display',
    owner: 'viewer',
    label: 'Fine near, coarse far',
    hint: 'Terrain and the grid fade with their distance from the action, so the eye goes where things happen.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'detail distance fade grid depth',
    tableDefault: true,
  },
  {
    key: 'displayXray',
    section: 'mine-display',
    owner: 'viewer',
    label: 'Never lose the actor',
    hint: 'Whatever stands between the eye and the actor or the target is drawn as a wireframe, and a shot swings a little to see round it when it can.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'wireframe xray occlusion behind wall see through swing',
    tableDefault: true,
  },
  {
    key: 'displayBreathing',
    section: 'mine-display',
    owner: 'viewer',
    label: 'The camera breathes at rest',
    hint:
      'A slow drift of a few degrees for a few seconds once nothing has happened. Off by default: every breath ' +
      'redraws the whole map, which a laptop feels. "Mode motion: Never" stops it too.',
    // Off since the perf overhaul (2026-10-28): breathing kept an idle laptop's main thread busy.
    control: { kind: 'toggle', defaultOn: false },
    keywords: 'camera drift breathing motion idle',
    tableDefault: true,
  },
  // The ambient marks (plan design-gaps, T4; the display page draws them all, always): each its own switch here.
  // (The range rings went on the user's word, 2026-10-21.)
  {
    key: 'displaySightWedge',
    section: 'mine-display',
    owner: 'viewer',
    label: 'The sight wedge toward the target',
    hint: 'A faint 60° wedge on the ground from whoever acts toward the one they target, 14 m long.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'sight wedge cone looking facing ambient',
    tableDefault: true,
  },
  {
    key: 'displayHeat',
    section: 'mine-display',
    owner: 'viewer',
    label: 'Heat under the other side',
    hint: "A red glow on the ground under everyone on a team other than the acting figure's.",
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'heat glow hostile enemy opposition ambient',
    tableDefault: true,
  },
  {
    key: 'displayScanlines',
    section: 'mine-display',
    owner: 'viewer',
    label: 'Scanlines',
    hint: 'Fine lines over the map, like an old screen.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'scanlines crt screen ambient',
    tableDefault: true,
  },
  {
    key: 'displayVignette',
    section: 'mine-display',
    owner: 'viewer',
    label: 'A dark frame round the map',
    hint: 'The edges of the map sink into the dark.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'vignette frame edges dark ambient',
    tableDefault: true,
  },
  {
    key: 'terrainAnimation',
    section: 'mine-map',
    owner: 'viewer',
    label: 'How terrain resolves as it appears',
    aria: 'Terrain animation',
    note: 'The default is already the least-motion answer there is.',
    control: { kind: 'enum', choices: TERRAIN_ANIMATIONS, fallback: 'none' },
    keywords: 'animation motion sweep rise bloom',
  },
  // The take-cover cue (2026-10-25): cover screens on the acting figure and the graded reach.
  {
    key: 'coverCue',
    section: 'mine-map',
    owner: 'viewer',
    label: 'Show the take-cover cue',
    hint: 'Curved screens on your acting figure toward each enemy gun, as tall as the cover he has against it, and his movement reach shaded by the cover each spot would give. Hover another of your figures to see his. Your screen only. Hidden when positions do not count or terrain adjudicates nothing.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'cover cue screens arcs threat reach take cover',
  },
  // How loud the movement bands are drawn (2026-10-21): the drawing only, never where a figure may go.
  {
    key: 'movementBands',
    section: 'mine-map',
    owner: 'viewer',
    label: 'How the movement bands are drawn',
    aria: 'Movement bands',
    hint: 'Your screen only: where anyone may go, and the move lines everyone sees, stay the same.',
    control: { kind: 'enum', choices: MOVEMENT_BAND_LOOKS, fallback: 'quiet' },
    keywords: 'movement bands rings reach half full charge run quiet intensity',
  },

  // ---- Mine: the log ----
  {
    key: 'view',
    section: 'mine-log',
    owner: 'viewer',
    label: 'Which log you are reading',
    aria: 'Combat log view',
    note: 'Solo play has no shared log, so the choice only takes effect in a session.',
    control: {
      kind: 'enum',
      choices: [
        { key: 'shared', label: "The table's", hint: 'Everything everyone did, in one thread.' },
        { key: 'local', label: 'Your own', hint: 'Only what happened on this screen.' },
      ],
      fallback: 'shared',
    },
    keywords: 'combat log shared local narrative',
  },
  {
    key: 'chatToasts',
    section: 'mine-log',
    owner: 'viewer',
    label: 'A toast for a private vox or a mention',
    aria: 'Vox toasts',
    note: 'A private vox always opens the log; this is the note that names who spoke.',
    control: { kind: 'toggle', defaultOn: true },
    keywords: 'vox chat toast whisper mention notification',
  },

  // ---- Mine: the screen ----
  {
    key: 'motion',
    section: 'mine-screen',
    owner: 'viewer',
    label: 'Motion when the mode or the danger changes',
    aria: 'Mode motion',
    hint: 'The lights cutting out, a flash, the ground easing, the beam at Searching.',
    note: 'If nothing ever moves: your system or browser is asking for reduced motion, and "Follow my system" obeys it. Choose "Always play".',
    control: { kind: 'enum', choices: [...MOTION_CHOICES], fallback: 'system' },
    keywords: 'motion animation transition flash accessibility mode reduced',
    // The GM may default it for the table - a fallback only; a viewer whose system disagrees is asked (MotionStrip).
    tableDefault: true,
  },
  {
    key: 'stealthBrightnessFloor',
    section: 'mine-screen',
    owner: 'viewer',
    label: 'Stealth is never fully dark',
    hint: 'Stealth draws on black, and the dark is part of it. On, the black is lifted to a deep blue-grey and the faintest text is brightened, so the screen stays readable in a lit room or on a dim display. Only your screen changes.',
    control: { kind: 'toggle', defaultOn: false },
    keywords: 'stealth dark brightness contrast readable accessibility black',
  },
  {
    key: 'bodyLens',
    section: 'mine-screen',
    owner: 'viewer',
    label: 'A lens over the body plate',
    hint: 'A loupe follows your pointer over a body plate and shows the figure three times larger, with every mark on it - for dense figures on a small screen. Clicks pass through it. Only your screen changes.',
    control: { kind: 'toggle', defaultOn: false },
    keywords: 'lens loupe magnify zoom body plate figure accessibility',
  },
];

/** The table's default for a viewer setting is a session ruling under this key (`displayFollow` → `tableDisplayFollow`). */
export const tableKeyOf = (viewerKey: string) => `table${viewerKey[0].toUpperCase()}${viewerKey.slice(1)}`;

/** The table's row for a viewer setting the GM may default. */
function tableRowFor(def: SettingDef): SettingDef {
  return {
    key: tableKeyOf(def.key),
    section: 'table-display',
    owner: 'session',
    label: def.label,
    ...(def.aria ? { aria: def.aria } : {}),
    ...(def.hint ? { hint: def.hint } : {}),
    note: "Where each screen starts: anyone's own choice in My display wins.",
    control: def.control,
    ...(def.keywords ? { keywords: `${def.keywords} table default` } : {}),
  };
}

/**
 * Every setting: the ones written above, then the table's display - one
 * session row per viewer row the GM may default, generated so the two
 * cannot drift.
 */
export const SETTINGS: SettingDef[] = [...ROWS, ...ROWS.filter((def) => def.tableDefault).map(tableRowFor)];

const BY_KEY = new Map(SETTINGS.map((s) => [s.key, s]));

/** The table's defaults off the house rules, keyed by the VIEWER's key; only what the GM has set. */
export function tableDefaultsFrom(houseRules: Record<string, unknown> | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const def of SETTINGS) {
    const stored = def.tableDefault ? houseRules?.[tableKeyOf(def.key)] : undefined;
    if (stored !== undefined) out[def.key] = stored;
  }
  return out;
}

/**
 * What a viewer setting comes to on this screen: the viewer's own choice;
 * with none, the table's default (`tableDefaultsFrom`); with neither, what
 * shipped. A toggle reads as a boolean, an enum as its validated key.
 */
export function viewerChoice(
  def: SettingDef,
  stored: unknown,
  tableDefaults: Record<string, unknown>,
): boolean | string {
  const chosen = stored !== undefined ? stored : tableDefaults[def.key];
  return def.control.kind === 'toggle' ? toggleChecked(def, chosen) : enumValue(def, chosen);
}

export function settingByKey(key: string): SettingDef | undefined {
  return BY_KEY.get(key);
}

/** The settings of one section, in registry order. */
export function settingsInSection(section: SettingSection): SettingDef[] {
  return SETTINGS.filter((s) => s.section === section);
}

/** The sections one owner offers, skipping any that ended up empty. */
export function sectionsFor(owner: SettingOwner): SectionDef[] {
  return SECTIONS.filter((s) => s.owner === owner && settingsInSection(s.id).length > 0);
}

/**
 * The rooms one viewer may actually walk into.
 *
 * A player sees only `Mine` - not a greyed-out copy of the GM's rulings.
 * Reading someone else's switches you cannot touch is not information,
 * it is furniture.
 */
export function visibleSections(canEditSession: boolean): SectionDef[] {
  return [...(canEditSession ? sectionsFor('session') : []), ...sectionsFor('viewer')];
}

/**
 * Is this setting currently at its default?
 *
 * `stored` is the raw value off the document (or the preference store)
 * - `undefined` means nobody has ever touched it, which is a default by
 * definition. An enum with a value this build has never heard of is a
 * default too: that is exactly what the resolvers do with it.
 */
/**
 * The session rulings that differ from their default, as `key=value` -
 * the one line of a bug report that says which rules were in play.
 */
export function changedSessionRules(houseRules: Record<string, unknown> | undefined): string[] {
  return SETTINGS.filter((def) => def.owner === 'session' && !isDefaultValue(def, houseRules?.[def.key])).map(
    (def) => `${def.key}=${JSON.stringify(houseRules?.[def.key])}`,
  );
}

export function isDefaultValue(def: SettingDef, stored: unknown): boolean {
  if (stored === undefined) return true;
  if (def.control.kind === 'toggle') return stored === def.control.defaultOn;
  const known = def.control.choices.some((c) => c.key === stored);
  return !known || stored === def.control.fallback;
}

/** What a toggle shows when the document says `stored`. */
export function toggleChecked(def: SettingDef, stored: unknown): boolean {
  if (def.control.kind !== 'toggle') return false;
  return def.control.defaultOn ? stored !== false : stored === true;
}

/** What an enum shows when the document says `stored` - validated, never trusted. */
export function enumValue(def: SettingDef, stored: unknown): string {
  if (def.control.kind !== 'enum') return '';
  return def.control.choices.some((c) => c.key === stored) ? (stored as string) : def.control.fallback;
}

/**
 * Settings whose label, hint or keywords match a typed query.
 *
 * Matches the HINT as well as the label deliberately: people look for a
 * rule by what it does to them ("cover", "burning") rather than by the
 * sentence it is filed under.
 */
export function searchSettings(query: string, pool: SettingDef[] = SETTINGS): SettingDef[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return pool.filter((s) => `${s.label} ${s.hint || ''} ${s.keywords || ''} ${s.key}`.toLowerCase().includes(q));
}
